import { notifyUnauthorized } from "../authSession";
import { genesisApiRoot } from "./genesisApiRoot";
import { genesisApiRootWithoutContentType } from "./genesisApiRoot";
import { genesisApiRootFile } from "./genesisApiRoot";
import { UNAUTHORIZED_APP_USER_DETAIL } from "@/constants/auth";

export interface ApiResponse<T = any> {
  success: boolean;
  status: number;
  data?: T;
  message?: string | { message: string };
}

type RequestFailure = { success: false; status: 0; message: string };

type AuthorizedFetchResult =
  | { ok: true; response: Response }
  | { ok: false; failure: RequestFailure };

function errorReason(error: unknown): string {
  return error instanceof Error && error.message ? error.message : "Network error";
}

/**
 * Builds the authorized request (token lookup) and performs the fetch.
 * Token and network errors are returned as a `status: 0` failure instead of
 * rejecting, so callers relying on the `ApiResponse` contract never hang.
 */
async function authorizedFetch(
  buildRequest: () => Promise<RequestInit & { url: string }>,
  init: RequestInit,
  errorMessage: string,
): Promise<AuthorizedFetchResult> {
  try {
    const { url, headers } = await buildRequest();
    const response = await fetch(url, { ...init, headers });
    return { ok: true, response };
  } catch (error) {
    return {
      ok: false,
      failure: {
        success: false,
        status: 0,
        message: `${errorMessage}: ${errorReason(error)}`,
      },
    };
  }
}

function parseErrorDetail(text: string): string | undefined {
  try {
    const json = text ? JSON.parse(text) : undefined;
    return typeof json?.detail === "string" ? json.detail : undefined;
  } catch {
    return undefined;
  }
}

/** 401 is checked on status alone so non-JSON 401s (e.g. gateway HTML) still log out. */
function notifyIfUnauthorized(status: number, detail?: string) {
  if (status === 401) {
    notifyUnauthorized("session_expired");
  } else if (status === 403 && detail === UNAUTHORIZED_APP_USER_DETAIL) {
    notifyUnauthorized("not_authorized");
  }
}

async function handleResponse<T>(
  response: Response,
  errorMessage: string,
): Promise<ApiResponse<T>> {
  const status = response.status;

  if (status === 204) {
    return {
      success: true,
      status: 204,
      data: null as unknown as T,
    };
  }

  // Checked before parsing so a non-JSON 401 still ends the session.
  if (status === 401) {
    notifyUnauthorized("session_expired");
  }

  let json: any;
  try {
    const text = await response.text();
    json = text ? JSON.parse(text) : undefined;
  } catch {
    return {
      success: false,
      status,
      message: response.ok
        ? `Unexpected response format (${status})`
        : `${errorMessage} (${status})`,
    };
  }

  if (!response.ok) {
    const detail = typeof json?.detail === "string" ? json.detail : undefined;

    if (status === 403 && detail === UNAUTHORIZED_APP_USER_DETAIL) {
      notifyUnauthorized("not_authorized");
    }

    return {
      success: false,
      status,
      message: json?.detail || `${errorMessage} (${status})`,
    };
  }

  return {
    success: true,
    status,
    data: json,
  };
}

async function jsonRequest<T>(
  buildRequest: () => Promise<RequestInit & { url: string }>,
  init: RequestInit,
  errorMessage: string,
): Promise<ApiResponse<T>> {
  const result = await authorizedFetch(buildRequest, init, errorMessage);
  if (!result.ok) {
    return result.failure;
  }
  return handleResponse<T>(result.response, errorMessage);
}

export async function apiGet<T = any>(
  endpoint: string,
  queryParams?: Record<string, string | number | boolean>,
): Promise<ApiResponse<T>> {
  const searchParams = queryParams
    ? `?${new URLSearchParams(queryParams as any)}`
    : "";

  return jsonRequest<T>(
    () => genesisApiRoot(`${endpoint}${searchParams}`),
    { method: "GET" },
    `GET ${endpoint} failed`,
  );
}

export async function apiGetById<T = any>(
  endpoint: string,
  id: number,
): Promise<ApiResponse<T>> {
  return jsonRequest<T>(
    () => genesisApiRoot(`${endpoint}/${id}`),
    { method: "GET" },
    `GET ${endpoint}/${id} failed`,
  );
}

export async function apiGetFile(
  endpoint: string,
  queryParams?: Record<string, string | number | boolean>,
): Promise<{ success: boolean; data?: Blob; message?: string }> {
  const searchParams = queryParams
    ? `?${new URLSearchParams(queryParams as any)}`
    : "";

  const result = await authorizedFetch(
    () => genesisApiRootFile(`${endpoint}${searchParams}`),
    { method: "GET" },
    `GET ${endpoint} failed`,
  );
  if (!result.ok) {
    return { success: false, message: result.failure.message };
  }
  const response = result.response;

  if (!response.ok) {
    notifyIfUnauthorized(response.status);
    return {
      success: false,
      message: `GET ${endpoint} failed: ${response.status} ${response.statusText}`,
    };
  }

  try {
    const blob = await response.blob();
    return { success: true, data: blob };
  } catch (error) {
    return {
      success: false,
      message: `GET ${endpoint} failed: ${errorReason(error)}`,
    };
  }
}

/**
 * Unlike the other helpers this one throws on failure; its caller
 * (DealWrapper ten-pager download) relies on try/catch.
 */
export async function apiGetByIdFile(
  endpoint: string,
  id: number,
  queryParams?: Record<string, string | number | boolean>,
): Promise<Blob> {
  const searchParams = queryParams
    ? `?${new URLSearchParams(queryParams as any)}`
    : "";
  const { url, headers } = await genesisApiRootFile(
    `${endpoint}/${id}${searchParams}`,
  );

  const response = await fetch(url, {
    method: "GET",
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    notifyIfUnauthorized(response.status, parseErrorDetail(errorText));
    throw new Error(`File download failed: ${response.status} - ${errorText}`);
  }

  return response.blob();
}

export async function apiPost<T = any>(
  endpoint: string,
  body: object,
): Promise<ApiResponse<T>> {
  return jsonRequest<T>(
    () => genesisApiRoot(endpoint),
    { method: "POST", body: JSON.stringify(body) },
    `POST ${endpoint} failed`,
  );
}

/** POST JSON body and expect a file (blob) in the response. */
export async function apiPostFile(
  endpoint: string,
  body: object,
): Promise<{ success: boolean; data?: Blob; message?: string }> {
  const result = await authorizedFetch(
    () => genesisApiRoot(endpoint),
    { method: "POST", body: JSON.stringify(body) },
    `POST ${endpoint} failed`,
  );
  if (!result.ok) {
    return { success: false, message: result.failure.message };
  }
  const response = result.response;

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    notifyIfUnauthorized(response.status, parseErrorDetail(errorText));
    return {
      success: false,
      message: `POST ${endpoint} failed: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ""}`,
    };
  }

  try {
    const blob = await response.blob();
    return { success: true, data: blob };
  } catch (error) {
    return {
      success: false,
      message: `POST ${endpoint} failed: ${errorReason(error)}`,
    };
  }
}

export async function apiFormPost<T = any>(
  endpoint: string,
  formData: FormData,
): Promise<ApiResponse<T>> {
  return jsonRequest<T>(
    () => genesisApiRootWithoutContentType(endpoint),
    { method: "POST", body: formData },
    `FORM POST ${endpoint} failed`,
  );
}

export async function apiPut<T = any>(
  endpoint: string,
  id: number,
  body: object,
  idNull?: boolean,
): Promise<ApiResponse<T>> {
  return jsonRequest<T>(
    () => genesisApiRoot(idNull ? endpoint : `${endpoint}/${id}`),
    { method: "PUT", body: JSON.stringify(body) },
    `PUT ${endpoint} failed`,
  );
}

export async function apiPatch<T = any>(
  endpoint: string,
  body: object,
): Promise<ApiResponse<T>> {
  return jsonRequest<T>(
    () => genesisApiRoot(endpoint),
    { method: "PATCH", body: JSON.stringify(body) },
    `PATCH ${endpoint} failed`,
  );
}

export async function apiDelete<T = any>(
  endpoint: string,
  id: number,
): Promise<ApiResponse<T>> {
  return jsonRequest<T>(
    () => genesisApiRoot(`${endpoint}/${id}`),
    { method: "DELETE" },
    `DELETE ${endpoint} failed`,
  );
}
